'use strict';

// DHA Gallery landing/browse experience ("The Approach", WI-12, see
// docs/work-items.md). Vanilla JS, no framework/build step — matches the
// project's existing pattern (dha-wheel-showroom.js, the lightbox in
// dha-gallery-lightbox.liquid). Starts from the JSON data island
// sections/dha-gallery-builds.liquid renders from real, currently
// published dha_gallery_build data (see that file's own comment), so the
// flow is usable the instant the page loads, and no dependency on
// PostAgent.
//
// Complete published set (WI-27): Liquid can only read the first 50
// published builds, so the island is just the first page. As soon as the
// script runs it asks assets/dha-gallery-index.js for the complete set
// (every page of templates/page.gallery-index.liquid), then replaces the
// working data, re-renders whatever step is open, appends the editorial
// tiles the server couldn't render, and re-checks the automatic featured
// build. Picks that can redirect (single-match skip-ahead, the ?wheel=
// deep link) wait for the complete set so a partial list can never send a
// customer to the wrong place.
//
// Only makes/models/wheels that appear in the parsed data can ever be
// offered as options — there is no separate hardcoded list to fall out of
// sync with what's actually published.
//
// Discovery skip-ahead (WI-18 for Vehicle, WI-19 for Wheel, see
// docs/work-items.md): a level with exactly one real option isn't a
// customer choice, so resolveVehicleResults()/enterVehicleFlow()
// (Vehicle) and resolveWheelResults() (Wheel) collapse straight
// through it — all the way to a single build's own Build Detail page
// when narrowing leaves exactly one real build. `state.backTo` (read
// by back(), overriding the static `backSteps` map) exists so Back
// always returns to the last step the customer actually saw, never to
// an intermediate level that was auto-skipped. The two paths share the
// same principle but not a common helper: Vehicle has two levels that
// can each collapse (make, then model) while Wheel has exactly one
// (the wheel picker itself is always shown), so a shared abstraction
// would cost more than the few lines of duplication it would save.
//
// A `?wheel=<code>` query param (WI-20) is read once on init and, if it
// names a real wheel in the complete data, immediately calls
// resolveWheelResults() for it — the entry point a wheel product page's
// "See this wheel in the real world" teaser links to when a wheel has
// multiple matching builds. Not a second discovery mechanism: it's the
// same function the wheel picker's own click handler already calls.
//
// Case-insensitive grouping (WI-26, see docs/work-items.md): real
// published data has carried both "AUDI" and "Audi" (same make, two
// casings) since the source metaobject field has no enforced casing.
// groupByCI() below groups make, model, and wheel by an uppercased key so
// any casing variant collapses into one option with a combined build
// count; the *displayed* label is picked from the real casing variants
// actually present (most builds win, a tie goes to whichever casing
// appeared first in the data), never an invented title-casing scheme —
// that would risk mangling a real acronym-style make/model (e.g. "BMW").
// Wheels are the one dimension with an independent source of truth for
// the "right" label: annotate() resolves each build's wheel against the
// published catalog with the same exact/unique-match-only title-tail rule
// WI-25 proved safe for Build Detail (now client-side, from the catalog
// the server ships, so builds beyond the first page get it too) and
// pickWheelLabel() prefers that catalog code. The same resolution gives
// each wheel its catalog series for the Wheel screen's series filter.
// Nothing here reads or writes Gallery Build data.
//
// Selection screens at scale (WI-27):
//  - Make / Model keep their photo-led, variable-width cards, but are laid
//    out as balanced rows inside a fixed width limit (balanceRows()) so a
//    growing list stays an even block instead of a ragged wrap, and their
//    density steps down as the list grows (data-density).
//  - The Wheel screen is a finder: a search box and series chips over a
//    compact, height-bounded grid. "All" keeps every wheel that has a
//    published build reachable (the grid scrolls; nothing is truncated).
//  - Every step is bounded to the hero and scrolls inside itself, so a long
//    list never buries the featured photo or runs off the hero.

(function () {
  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  function normKey(value) {
    return value == null ? '' : String(value).trim().toUpperCase();
  }

  // Wheel identity for catalog matching: upcased, all whitespace removed —
  // the same normalisation Build Detail's title-tail matcher uses.
  function wheelKey(value) {
    return normKey(value).replace(/\s+/g, '');
  }

  // Picks a display label from the real casing variants seen for a
  // group: whichever variant occurs on the most builds; ties go to
  // whichever variant appeared first in the source data. Never
  // synthesizes a new casing — always one of the real values.
  function pickLabel(rawValues) {
    var counts = {};
    var order = [];
    for (var i = 0; i < rawValues.length; i++) {
      var v = rawValues[i];
      if (!Object.prototype.hasOwnProperty.call(counts, v)) {
        counts[v] = 0;
        order.push(v);
      }
      counts[v]++;
    }
    var best = order[0];
    for (var j = 1; j < order.length; j++) {
      if (counts[order[j]] > counts[best]) best = order[j];
    }
    return best;
  }

  // Groups `items` by the case-insensitive value `keyFn` returns,
  // preserving first-appearance order of each distinct key (so make/
  // model discovery ordering is unchanged by the case merge). `labelFn`
  // (optional) can override the default most-common-casing label — used
  // by wheel grouping to prefer a Shopify-resolved canonical code.
  function groupByCI(items, keyFn, labelFn) {
    var order = [];
    var byKey = {};
    for (var i = 0; i < items.length; i++) {
      var raw = keyFn(items[i]);
      if (raw == null || raw === '') continue;
      var key = normKey(raw);
      if (!byKey[key]) {
        byKey[key] = { key: key, builds: [], rawValues: [] };
        order.push(key);
      }
      byKey[key].builds.push(items[i]);
      byKey[key].rawValues.push(raw);
    }
    return order.map(function (key) {
      var g = byKey[key];
      var label = labelFn ? labelFn(g) : pickLabel(g.rawValues);
      return { key: g.key, label: label, builds: g.builds };
    });
  }

  // Prefers the catalog-resolved canonical code (see annotate()) shared by
  // every build in the group; falls back to the most-common raw casing
  // when no unique catalog match exists.
  function pickWheelLabel(group) {
    for (var i = 0; i < group.builds.length; i++) {
      if (group.builds[i].wheelCanonical) return group.builds[i].wheelCanonical;
    }
    return pickLabel(group.rawValues);
  }

  // Natural/alphanumeric sort for wheel codes (e.g. UF2 before UF101,
  // not after) so the wheel picker stays easy to scan as codes grow.
  function naturalCompare(a, b) {
    var chunkRe = /(\d+)|(\D+)/g;
    var ax = String(a).match(chunkRe) || [];
    var bx = String(b).match(chunkRe) || [];
    var len = Math.max(ax.length, bx.length);
    for (var i = 0; i < len; i++) {
      var av = ax[i];
      var bv = bx[i];
      if (av === undefined) return -1;
      if (bv === undefined) return 1;
      var an = /^\d+$/.test(av);
      var bn = /^\d+$/.test(bv);
      if (an && bn) {
        var diff = parseInt(av, 10) - parseInt(bv, 10);
        if (diff !== 0) return diff;
        if (av.length !== bv.length) return av.length - bv.length;
      } else if (av !== bv) {
        return av < bv ? -1 : 1;
      }
    }
    return 0;
  }

  // Resolves each build's wheel against the published catalog ({series,
  // code} pairs shipped by the server): a build gets `wheelCanonical` (the
  // catalog's own code) and `series` only when EXACTLY ONE product matches
  // its wheel — never a guess. A wheel with no catalog product (e.g. a
  // retired wheel) still gets a series when its letter prefix ("UF", "FT")
  // maps to one series in the catalog, so it isn't stranded outside the
  // series filter; otherwise its series stays blank ("Other").
  function annotate(list, catalog) {
    var byKey = {};
    var prefixCounts = {};
    (catalog || []).forEach(function (entry) {
      var key = wheelKey(entry.code);
      if (!key) return;
      (byKey[key] = byKey[key] || []).push(entry);
      var m = /^[A-Z]+/.exec(key);
      if (m && entry.series) {
        var counts = (prefixCounts[m[0]] = prefixCounts[m[0]] || {});
        counts[entry.series] = (counts[entry.series] || 0) + 1;
      }
    });
    var prefixSeries = {};
    Object.keys(prefixCounts).forEach(function (prefix) {
      var best = null;
      Object.keys(prefixCounts[prefix]).forEach(function (series) {
        if (best === null || prefixCounts[prefix][series] > prefixCounts[prefix][best]) best = series;
      });
      prefixSeries[prefix] = best;
    });
    list.forEach(function (b) {
      var key = wheelKey(b.wheel);
      var matches = byKey[key];
      if (matches && matches.length === 1) {
        b.wheelCanonical = matches[0].code;
        b.series = matches[0].series || '';
      } else {
        b.wheelCanonical = '';
        b.series = '';
      }
      if (!b.series) {
        var m = /^[A-Z]+/.exec(key);
        b.series = (m && prefixSeries[m[0]]) || '';
      }
    });
  }

  function seriesOfGroup(group) {
    for (var i = 0; i < group.builds.length; i++) {
      if (group.builds[i].series) return group.builds[i].series;
    }
    return '';
  }

  function seriesLabel(series) {
    return series ? series.replace(/\s+Series$/i, '') : 'Other';
  }

  function plural(n) {
    return n + (n === 1 ? ' build' : ' builds');
  }

  function densityFor(count) {
    if (count <= 8) return 'roomy';
    if (count <= 16) return 'cozy';
    return 'compact';
  }

  // Splits `widths` (in order) into the fewest rows that each fit in `max`
  // (given `gap` between items) with the most even fill: minimises the sum
  // of squared leftover space per row. Tries the fewest possible rows
  // first, adding rows only if an item layout can't fit. Small n (a few
  // dozen options), so a plain O(rows · n²) dynamic program is plenty.
  function partitionRows(widths, max, gap) {
    var n = widths.length;
    if (!n) return [];
    var prefix = [0];
    for (var i = 0; i < n; i++) prefix.push(prefix[i] + widths[i]);
    function rowWidth(from, to) {
      return prefix[to] - prefix[from] + gap * (to - from - 1);
    }
    var total = rowWidth(0, n);
    var startRows = Math.max(1, Math.ceil(total / max));
    for (var rows = startRows; rows <= n; rows++) {
      var INF = Infinity;
      var cost = [];
      var cut = [];
      for (var r = 0; r <= rows; r++) {
        cost.push(new Array(n + 1).fill(INF));
        cut.push(new Array(n + 1).fill(0));
      }
      cost[0][0] = 0;
      for (var rr = 1; rr <= rows; rr++) {
        for (var to = rr; to <= n; to++) {
          for (var from = rr - 1; from < to; from++) {
            if (cost[rr - 1][from] === INF) continue;
            var w = rowWidth(from, to);
            if (w > max) continue;
            var slack = max - w;
            var c = cost[rr - 1][from] + slack * slack;
            if (c < cost[rr][to]) {
              cost[rr][to] = c;
              cut[rr][to] = from;
            }
          }
        }
      }
      if (cost[rows][n] !== INF) {
        var out = [];
        var end = n;
        for (var k = rows; k >= 1; k--) {
          var begin = cut[k][end];
          out.unshift([begin, end]);
          end = begin;
        }
        return out;
      }
    }
    return widths.map(function (_, idx) { return [idx, idx + 1]; });
  }

  function escapeHtml(value) {
    var div = document.createElement('div');
    div.textContent = value == null ? '' : String(value);
    return div.innerHTML;
  }

  function initGallery(root) {
    var dataEl = root.querySelector('[data-dha-gl-data]');
    if (!dataEl) return;

    var island;
    try {
      island = JSON.parse(dataEl.textContent || '[]');
    } catch (error) {
      return;
    }
    var builds = Array.isArray(island) ? island : (island.builds || []);
    var catalog = Array.isArray(island) ? [] : (island.catalog || []);
    if (!builds.length) return;
    annotate(builds, catalog);

    var hero = root.querySelector('.dha-gl__hero');
    var backdrop = root.querySelector('[data-dha-gl-backdrop]');
    var flow = root.querySelector('[data-dha-gl-flow]');
    var sheet = root.querySelector('[data-dha-gl-sheet]');
    var titleEl = root.querySelector('[data-dha-gl-title]');
    var subEl = root.querySelector('[data-dha-gl-sub]');
    var crumbEl = root.querySelector('[data-dha-gl-crumb]');
    var optionsEl = root.querySelector('[data-dha-gl-options]');
    var resultsEl = root.querySelector('[data-dha-gl-results]');
    var streamEl = root.querySelector('[data-dha-gl-stream]');
    if (!hero || !backdrop || !flow || !sheet) return;

    var defaultBackdropImage = backdrop.style.backgroundImage;
    var state = { step: 'hero' };
    var lastFocused = null;
    var complete = false;
    var whenCompleteQueue = [];
    // Wheel finder state survives a Back from the wheel's results, and is
    // reset when the flow closes.
    var wheelUi = { query: '', series: 'all' };
    var mobileMq = window.matchMedia ? window.matchMedia('(max-width: 860px)') : null;

    function isMobile() {
      return !!(mobileMq && mobileMq.matches);
    }

    function setBackdrop(url) {
      backdrop.style.backgroundImage = url ? "url('" + url + "')" : defaultBackdropImage;
    }

    // Runs `fn` once the complete published set has loaded (immediately if
    // it already has). Used for anything that can redirect or otherwise
    // commit the customer based on the full data.
    function whenComplete(fn) {
      if (complete) fn();
      else whenCompleteQueue.push(fn);
    }

    function renderOption(o, compact) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'dha-gl__option' + (compact ? ' dha-gl__option--compact' : '');
      var thumb = document.createElement('span');
      thumb.className = 'dha-gl__option-thumb';
      thumb.style.backgroundImage = "url('" + o.thumb + "')";
      var label = document.createElement('span');
      label.className = 'dha-gl__option-label';
      label.textContent = o.label;
      var count = document.createElement('span');
      count.className = 'dha-gl__option-count';
      count.textContent = o.count;
      var arrow = document.createElement('span');
      arrow.className = 'dha-gl__option-arrow';
      arrow.setAttribute('aria-hidden', 'true');
      arrow.textContent = '→';
      btn.appendChild(thumb);
      if (compact) {
        // Label and count stack so a small card stays compact.
        var text = document.createElement('span');
        text.className = 'dha-gl__option-text';
        text.appendChild(label);
        text.appendChild(count);
        btn.appendChild(text);
      } else {
        btn.appendChild(label);
        btn.appendChild(count);
      }
      btn.appendChild(arrow);
      btn.addEventListener('click', o.onPick);
      btn.addEventListener('mouseenter', function () { setBackdrop(o.backdrop); });
      btn.addEventListener('focus', function () { setBackdrop(o.backdrop); });
      btn.addEventListener('mouseleave', function () { setBackdrop(currentDefaultBackdrop); });
      return btn;
    }

    function renderResult(r) {
      var a = document.createElement('a');
      a.className = 'dha-gl__result';
      a.href = r.build.url;
      a.style.backgroundImage = "url('" + r.build.card + "')";
      var inner = document.createElement('div');
      inner.className = 'dha-gl__result-inner';
      var vehicle = document.createElement('div');
      vehicle.className = 'dha-gl__result-vehicle';
      vehicle.textContent = r.build.make + ' ' + r.build.model;
      inner.appendChild(vehicle);
      if (r.sub) {
        var sub = document.createElement('div');
        sub.className = 'dha-gl__result-sub';
        sub.textContent = r.sub;
        inner.appendChild(sub);
      }
      a.appendChild(inner);
      return a;
    }

    function renderQuoteCard() {
      var card = document.createElement('div');
      card.className = 'dha-gl__result dha-gl__result--quote';
      card.innerHTML =
        '<div class="dha-gl__result-inner">' +
        '<div class="dha-gl__quote-title">Not the exact car you run?</div>' +
        '<div class="dha-gl__quote-body">Send us your vehicle and we’ll confirm fitment and finishes directly.</div>' +
        '<button type="button" class="dha-gl__quote-cta" data-dha-quote-trigger>Request a Quote →</button>' +
        '</div>';
      return card;
    }

    var currentDefaultBackdrop = null;

    // Shared by both the make-level auto-skip and a real customer model
    // pick: a model with exactly one matching build is itself not a
    // choice, so go straight to that build's Build Detail page instead
    // of opening a one-card results screen. `modelGroup.builds` is
    // already the case-insensitively grouped set for this make+model,
    // so it already combines every casing variant.
    function resolveVehicleResults(makeGroup, modelGroup, backTo) {
      var matches = modelGroup.builds;
      if (matches.length === 1) {
        window.location.href = matches[0].url;
        return;
      }
      open({ step: 'vehicleResults', makeGroup: makeGroup, modelGroup: modelGroup, backTo: backTo });
    }

    // Called when a make is picked from the top-level make list. Skips
    // the model step entirely when the make has only one real model
    // (case-insensitively grouped). Waits for the complete set first: a
    // make that looks single-model in a partial list must not skip ahead.
    function enterVehicleFlow(pickedGroup) {
      whenComplete(function () {
        var makeGroup = freshMakeGroup(pickedGroup.key) || pickedGroup;
        var modelGroups = groupByCI(makeGroup.builds, function (b) { return b.model; });
        if (modelGroups.length > 1) {
          open({ step: 'model', makeGroup: makeGroup });
          return;
        }
        resolveVehicleResults(makeGroup, modelGroups[0], 'make');
      });
    }

    // Called when a wheel is picked from the wheel list (WI-19, see
    // docs/work-items.md). The wheel picker itself is always shown —
    // picking a wheel is the meaningful choice — but a wheel used by
    // only one real build has no further choice to make, so skip the
    // one-card results screen and go straight to that build. No
    // `backTo` needed: unlike Vehicle, there's no earlier level that
    // could have been auto-skipped on the way here, so the existing
    // `wheelResults -> wheel` mapping in `backSteps` is always correct.
    // `wheelGroup.builds` is already the case-insensitively grouped set.
    function resolveWheelResults(pickedGroup) {
      whenComplete(function () {
        var wheelGroup = freshWheelGroup(pickedGroup.key) || pickedGroup;
        var matches = wheelGroup.builds;
        if (matches.length === 1) {
          window.location.href = matches[0].url;
          return;
        }
        open({ step: 'wheelResults', wheelGroup: wheelGroup });
      });
    }

    function makeGroups() {
      return groupByCI(builds, function (b) { return b.make; });
    }

    function wheelGroups() {
      return groupByCI(builds, function (b) { return b.wheel; }, pickWheelLabel);
    }

    function freshMakeGroup(key) {
      var groups = makeGroups();
      for (var i = 0; i < groups.length; i++) if (groups[i].key === key) return groups[i];
      return null;
    }

    function freshWheelGroup(key) {
      var groups = wheelGroups();
      for (var i = 0; i < groups.length; i++) if (groups[i].key === key) return groups[i];
      return null;
    }

    function buildView() {
      var s = state;
      var options = null;
      var results = null;
      var title = '';
      var sub = '';
      var crumb = '';
      var kind = null;

      if (s.step === 'make') {
        crumb = 'VEHICLE';
        title = 'Which make?';
        sub = 'Only makes with a published build are listed.';
        kind = 'make';
        options = makeGroups().map(function (g) {
          var sample = g.builds[0];
          return {
            label: g.label,
            count: plural(g.builds.length),
            thumb: sample.thumb,
            backdrop: sample.backdrop,
            onPick: function () { enterVehicleFlow(g); }
          };
        });
      } else if (s.step === 'model') {
        crumb = 'VEHICLE / ' + s.makeGroup.label.toUpperCase();
        title = s.makeGroup.label + ' — which model?';
        sub = 'These are the platforms we have actually fitted.';
        kind = 'model';
        var modelGroups = groupByCI(s.makeGroup.builds, function (b) { return b.model; });
        options = modelGroups.map(function (g) {
          var sample = g.builds[0];
          return {
            label: g.label,
            count: plural(g.builds.length),
            thumb: sample.thumb,
            backdrop: sample.backdrop,
            onPick: function () { resolveVehicleResults(s.makeGroup, g, undefined); }
          };
        });
      } else if (s.step === 'vehicleResults') {
        crumb = 'VEHICLE / ' + s.makeGroup.label.toUpperCase() + ' / ' + s.modelGroup.label.toUpperCase();
        var vBuilds = s.modelGroup.builds;
        title = vBuilds.length === 1 ? 'One documented ' + s.modelGroup.label + ' build.' : vBuilds.length + ' documented ' + s.modelGroup.label + ' builds.';
        sub = 'Open a build for the full photo set, or ask us about your own car.';
        results = vBuilds.map(function (b) { return { build: b, sub: b.wheel }; });
      } else if (s.step === 'wheel') {
        crumb = 'WHEEL';
        title = 'Which wheel?';
        sub = 'Every wheel shown here appears on at least one real car in the Gallery.';
        kind = 'wheel';
        var groups = wheelGroups();
        groups.sort(function (a, b) { return naturalCompare(a.label, b.label); });
        options = groups.map(function (g) {
          var sample = g.builds[0];
          return {
            label: g.label,
            key: g.key,
            series: seriesOfGroup(g),
            count: plural(g.builds.length),
            thumb: sample.thumb,
            backdrop: sample.backdrop,
            onPick: function () { resolveWheelResults(g); }
          };
        });
      } else if (s.step === 'wheelResults') {
        crumb = 'WHEEL / ' + s.wheelGroup.label.toUpperCase();
        var wBuilds = s.wheelGroup.builds;
        title = s.wheelGroup.label + ' on ' + (wBuilds.length === 1 ? 'one real car.' : wBuilds.length + ' real cars.');
        sub = 'Open a build for the full photo set, or take the wheel through to a quote.';
        results = wBuilds.map(function (b) {
          var subText = b.finish && b.size ? b.finish + ' — ' + b.size : (b.finish || b.size || null);
          return { build: b, sub: subText };
        });
      }

      return { title: title, sub: sub, crumb: crumb, kind: kind, options: options, results: results };
    }

    // Moves every option back into the container as direct children, in
    // order, dropping any balanced-row wrappers.
    function flattenOptions() {
      var btns = Array.prototype.slice.call(optionsEl.querySelectorAll('.dha-gl__option'));
      optionsEl.setAttribute('data-layout', 'flow');
      btns.forEach(function (b) { optionsEl.appendChild(b); });
      Array.prototype.slice.call(optionsEl.querySelectorAll('.dha-gl__row')).forEach(function (r) {
        if (r.parentNode) r.parentNode.removeChild(r);
      });
      return btns;
    }

    // Balanced rows for the Make / Model screens (desktop): the cards keep
    // their natural, variable widths, but are dealt into the fewest rows
    // that fit the container's width limit with the most even fill, and
    // each row's cards then share the leftover space so every row is the
    // same width. Mobile keeps its stacked list (CSS), so nothing to do.
    function balanceRows() {
      var btns = flattenOptions();
      if (isMobile() || btns.length < 2) return;
      // 1px of headroom so sub-pixel rounding can never overflow a row.
      var max = optionsEl.clientWidth - 1;
      if (max <= 0) return;
      var gap = parseFloat(window.getComputedStyle(optionsEl).columnGap) || 10;
      var widths = btns.map(function (b) { return Math.ceil(b.getBoundingClientRect().width); });
      var rows = partitionRows(widths, max, gap);
      optionsEl.setAttribute('data-layout', 'rows');
      rows.forEach(function (range) {
        var row = document.createElement('div');
        row.className = 'dha-gl__row';
        for (var i = range[0]; i < range[1]; i++) row.appendChild(btns[i]);
        optionsEl.appendChild(row);
      });
    }

    // Wheel finder: search box + series chips over a compact grid. All
    // cards are rendered once; search and series just toggle `hidden`, so
    // "All" is always the complete set and nothing is ever cut off.
    function renderWheelFinder(options) {
      var seriesCounts = {};
      var seriesOrder = [];
      options.forEach(function (o) {
        var label = seriesLabel(o.series);
        if (!Object.prototype.hasOwnProperty.call(seriesCounts, label)) {
          seriesCounts[label] = 0;
          seriesOrder.push(label);
        }
        seriesCounts[label]++;
      });
      seriesOrder.sort(function (a, b) {
        if (a === 'Other') return 1;
        if (b === 'Other') return -1;
        return seriesCounts[b] - seriesCounts[a] || (a < b ? -1 : 1);
      });
      if (wheelUi.series !== 'all' && !Object.prototype.hasOwnProperty.call(seriesCounts, wheelUi.series)) {
        wheelUi.series = 'all';
      }

      var tools = document.createElement('div');
      tools.className = 'dha-gl__finder';

      var searchWrap = document.createElement('label');
      searchWrap.className = 'dha-gl__search';
      var searchLabel = document.createElement('span');
      searchLabel.className = 'dha-gl__sr-only';
      searchLabel.textContent = 'Find a wheel by model';
      var input = document.createElement('input');
      input.type = 'search';
      input.className = 'dha-gl__search-input';
      input.placeholder = 'Find a wheel — try “142”';
      input.autocomplete = 'off';
      input.setAttribute('autocapitalize', 'characters');
      input.setAttribute('enterkeyhint', 'go');
      input.spellcheck = false;
      input.value = wheelUi.query;
      searchWrap.appendChild(searchLabel);
      searchWrap.appendChild(input);
      tools.appendChild(searchWrap);

      var chipButtons = [];
      if (seriesOrder.length > 1) {
        var chips = document.createElement('div');
        chips.className = 'dha-gl__chips';
        chips.setAttribute('role', 'group');
        chips.setAttribute('aria-label', 'Wheel series');
        var makeChip = function (value, text, count) {
          var chip = document.createElement('button');
          chip.type = 'button';
          chip.className = 'dha-gl__chip';
          chip.setAttribute('data-series', value);
          chip.innerHTML = escapeHtml(text) + ' <span class="dha-gl__chip-count">' + count + '</span>';
          chip.addEventListener('click', function () {
            wheelUi.series = value;
            applyFilter();
          });
          chips.appendChild(chip);
          chipButtons.push(chip);
        };
        makeChip('all', 'All', options.length);
        seriesOrder.forEach(function (label) { makeChip(label, label, seriesCounts[label]); });
        tools.appendChild(chips);
      }

      var status = document.createElement('p');
      status.className = 'dha-gl__finder-status';
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      tools.appendChild(status);

      var grid = document.createElement('div');
      grid.className = 'dha-gl__grid';
      var cards = options.map(function (o) {
        var card = renderOption(o, true);
        card.setAttribute('data-key', o.key.replace(/\s+/g, ''));
        card.setAttribute('data-series', seriesLabel(o.series));
        grid.appendChild(card);
        return card;
      });

      var none = document.createElement('p');
      none.className = 'dha-gl__finder-none';
      none.hidden = true;
      none.textContent = 'No published build uses that wheel. Try another model number, or clear the search.';

      function visibleCards() {
        return cards.filter(function (c) { return !c.hidden; });
      }

      // Soft fade on the grid's bottom edge while more wheels are hidden
      // below the fold of its scroll area (desktop; on mobile the grid
      // doesn't scroll on its own).
      function updateFade() {
        var more = grid.scrollHeight - grid.scrollTop - grid.clientHeight > 4;
        if (more) grid.setAttribute('data-scroll', 'more');
        else grid.removeAttribute('data-scroll');
      }
      grid.addEventListener('scroll', updateFade);

      function applyFilter() {
        var q = wheelKey(wheelUi.query);
        cards.forEach(function (c) {
          var okSeries = wheelUi.series === 'all' || c.getAttribute('data-series') === wheelUi.series;
          var okQuery = !q || c.getAttribute('data-key').indexOf(q) !== -1;
          c.hidden = !(okSeries && okQuery);
        });
        chipButtons.forEach(function (chip) {
          chip.setAttribute('aria-pressed', chip.getAttribute('data-series') === wheelUi.series ? 'true' : 'false');
        });
        var shown = visibleCards().length;
        none.hidden = shown !== 0;
        grid.hidden = shown === 0;
        status.textContent = shown === cards.length
          ? cards.length + (cards.length === 1 ? ' wheel' : ' wheels')
          : shown + ' of ' + cards.length + ' wheels';
        // Show a matching wheel's photo as the backdrop while narrowing.
        var first = visibleCards()[0];
        var match = first ? options[cards.indexOf(first)] : null;
        currentDefaultBackdrop = match ? match.backdrop : (options[0] && options[0].backdrop);
        setBackdrop(currentDefaultBackdrop);
        grid.scrollTop = 0;
        updateFade();
      }

      input.addEventListener('input', function () {
        wheelUi.query = input.value;
        applyFilter();
      });
      input.addEventListener('keydown', function (event) {
        if (event.key === 'Enter') {
          var visible = visibleCards();
          if (visible.length === 1) {
            event.preventDefault();
            visible[0].click();
          }
        }
      });

      optionsEl.appendChild(tools);
      optionsEl.appendChild(grid);
      optionsEl.appendChild(none);
      applyFilter();
      window.requestAnimationFrame(updateFade);
      return input;
    }

    var wheelInput = null;

    function render() {
      var view = buildView();

      titleEl.textContent = view.title;
      subEl.textContent = view.sub;
      crumbEl.textContent = view.crumb;

      optionsEl.innerHTML = '';
      resultsEl.innerHTML = '';
      wheelInput = null;
      flow.setAttribute('data-step', state.step);

      if (view.results) {
        optionsEl.hidden = true;
        resultsEl.hidden = false;
        view.results.forEach(function (r) { resultsEl.appendChild(renderResult(r)); });
        if (view.results.length < 3) resultsEl.appendChild(renderQuoteCard());
        currentDefaultBackdrop = view.results[0] ? view.results[0].build.backdrop : null;
        setBackdrop(currentDefaultBackdrop);
      } else if (view.options) {
        optionsEl.hidden = false;
        resultsEl.hidden = true;
        optionsEl.setAttribute('data-kind', view.kind);
        optionsEl.setAttribute('data-density', view.kind === 'wheel' ? 'wheel' : densityFor(view.options.length));
        if (view.kind === 'wheel') {
          optionsEl.setAttribute('data-layout', 'finder');
          wheelInput = renderWheelFinder(view.options);
        } else {
          view.options.forEach(function (o) { optionsEl.appendChild(renderOption(o, false)); });
          currentDefaultBackdrop = view.options[0] ? view.options[0].backdrop : null;
          setBackdrop(currentDefaultBackdrop);
          balanceRows();
        }
      }
    }

    function open(nextState) {
      state = nextState;
      var wasOpen = !flow.hidden;
      if (!wasOpen) {
        lastFocused = document.activeElement;
        hero.setAttribute('data-state', 'flow');
        flow.hidden = false;
        document.addEventListener('keydown', onKeydown, true);
      }
      render();
      // Move focus into the panel once it's actually visible/populated —
      // the back button is always present and is the natural first stop.
      // On the wheel finder with a real pointer, the search box is the
      // natural first stop instead; touch devices keep Back so the on-screen
      // keyboard doesn't cover the list.
      var backBtn = root.querySelector('[data-dha-gl-back]');
      var canHover = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      if (wheelInput && canHover && !isMobile()) wheelInput.focus();
      else if (backBtn) backBtn.focus();
      else sheet.focus();
    }

    var backSteps = { make: 'hero', model: 'make', vehicleResults: 'model', wheel: 'hero', wheelResults: 'wheel' };

    function close() {
      hero.removeAttribute('data-state');
      flow.hidden = true;
      state = { step: 'hero' };
      wheelUi = { query: '', series: 'all' };
      setBackdrop(null);
      document.removeEventListener('keydown', onKeydown, true);
      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    }

    function back() {
      // `state.backTo` (set by resolveVehicleResults()/enterVehicleFlow()
      // when a level was auto-skipped, see the file header) takes
      // priority over the static map, so Back returns to the last step
      // the customer actually saw rather than a skipped intermediate one.
      var prev = state.backTo || backSteps[state.step] || 'hero';
      if (prev === 'hero') {
        close();
      } else {
        // Only `makeGroup` ever needs to survive a back-step
        // (vehicleResults -> model still needs to know which make);
        // every other step rebuilds its option list from the full data
        // set regardless.
        state = { step: prev, makeGroup: state.makeGroup };
        render();
      }
    }

    function onKeydown(event) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        // In the wheel search box, Escape first clears the text.
        if (event.target && event.target.classList && event.target.classList.contains('dha-gl__search-input') && event.target.value) {
          event.preventDefault();
          event.target.value = '';
          wheelUi.query = '';
          event.target.dispatchEvent(new Event('input'));
          return;
        }
        event.preventDefault();
        close();
      }
    }

    root.addEventListener('click', function (event) {
      var opener = event.target.closest('[data-dha-gl-open]');
      if (opener) {
        open({ step: opener.getAttribute('data-dha-gl-open') });
        return;
      }
      if (event.target.closest('[data-dha-gl-close]')) {
        close();
        return;
      }
      if (event.target.closest('[data-dha-gl-back]')) {
        back();
        return;
      }
      // Clicking the darkened area outside the sheet/panel (but not
      // inside it) closes the flow, same as the persistent CLOSE control.
      if (event.target === flow || event.target.classList.contains('dha-gl__flow-scrim')) {
        close();
      }
    });

    // Re-balance the Make / Model rows when the viewport changes.
    var resizeTimer = null;
    window.addEventListener('resize', function () {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(rebalanceIfOptionsOpen, 120);
    });
    // Card widths depend on the web font, which loads lazily on first use
    // (after the first measurement) — re-balance whenever fonts finish.
    function rebalanceIfOptionsOpen() {
      if (flow.hidden || !optionsEl || optionsEl.hidden) return;
      var kind = optionsEl.getAttribute('data-kind');
      if (kind === 'make' || kind === 'model') balanceRows();
    }
    if (document.fonts) {
      if (document.fonts.ready) document.fonts.ready.then(rebalanceIfOptionsOpen);
      if (document.fonts.addEventListener) document.fonts.addEventListener('loadingdone', rebalanceIfOptionsOpen);
    }

    // ── Complete published set (WI-27) ────────────────────────────────

    function buildTile(b) {
      var wrap = document.createElement('div');
      wrap.className = 'dha-gl__tile';
      var a = document.createElement('a');
      a.className = 'dha-gl__tile-inner';
      a.href = b.url;
      if (b.tile) {
        var img = document.createElement('img');
        img.className = 'dha-gl__tile-img' + (b.contain ? ' dha-gl__tile-img--contain' : '');
        img.src = b.tile;
        if (b.tileSet) img.srcset = b.tileSet;
        img.sizes = '(max-width: 860px) 100vw, (max-width: 1100px) 50vw, 33vw';
        img.alt = b.make + ' ' + b.model + ' — ' + b.wheel;
        img.loading = 'lazy';
        img.width = 900;
        img.height = 600;
        a.appendChild(img);
      }
      var body = document.createElement('div');
      body.className = 'dha-gl__tile-body';
      var left = document.createElement('div');
      var vehicle = document.createElement('div');
      vehicle.className = 'dha-gl__tile-vehicle';
      vehicle.textContent = b.make + ' ' + b.model;
      var wheel = document.createElement('div');
      wheel.className = 'dha-gl__tile-wheel';
      wheel.textContent = b.wheel;
      left.appendChild(vehicle);
      left.appendChild(wheel);
      var count = document.createElement('div');
      count.className = 'dha-gl__tile-count';
      count.textContent = b.count + ' PHOTO' + (b.count === 1 ? '' : 'S');
      body.appendChild(left);
      body.appendChild(count);
      a.appendChild(body);
      wrap.appendChild(a);
      return wrap;
    }

    // The server renders only the first page of the editorial stream;
    // append a tile for every build it couldn't (matched by URL).
    function appendMissingTiles() {
      if (!streamEl) return;
      var present = {};
      Array.prototype.forEach.call(streamEl.querySelectorAll('.dha-gl__tile-inner'), function (a) {
        present[a.getAttribute('href')] = true;
      });
      builds.forEach(function (b) {
        if (!present[b.url]) streamEl.appendChild(buildTile(b));
      });
    }

    // The automatic featured build is "highest source_build_id", which the
    // server could only judge across the first page. Once the complete set
    // is known, swap the hero to the true pick — or, if a merchant-selected
    // build turns out not to be published, to the automatic pick.
    function reconcileFeatured() {
      if (!flow.hidden) return;
      var renderedId = hero.getAttribute('data-featured-id');
      var configSid = hero.getAttribute('data-featured-config-sid');
      var target = null;
      if (configSid) {
        for (var i = 0; i < builds.length; i++) {
          if (String(builds[i].sid) === configSid) { target = builds[i]; break; }
        }
      }
      if (!target) {
        var best = -1;
        for (var j = 0; j < builds.length; j++) {
          var n = parseInt(builds[j].id, 10);
          if (!isNaN(n) && n > best) { best = n; target = builds[j]; }
        }
      }
      if (!target || String(target.id) === renderedId) return;

      var preload = new Image();
      preload.onload = function () {
        if (!flow.hidden) return;
        defaultBackdropImage = "url('" + target.backdrop + "')";
        setBackdrop(null);
        hero.setAttribute('data-featured-id', String(target.id));
        var title = root.querySelector('.dha-gl__title');
        if (title) title.textContent = target.make + ' ' + target.model;
        var metaText = target.finish && target.size ? target.finish + ' — ' + target.size : (target.finish || target.size || '');
        var meta = root.querySelector('.dha-gl__meta');
        if (meta) {
          meta.textContent = metaText;
          meta.hidden = !metaText;
        } else if (metaText) {
          var main = root.querySelector('.dha-gl__hero-main');
          if (main) {
            meta = document.createElement('div');
            meta.className = 'dha-gl__meta';
            meta.textContent = metaText;
            main.appendChild(meta);
          }
        }
      };
      preload.src = target.backdrop;
    }

    // Keeps an open step consistent after the data set grows: rebuild the
    // step's groups from the complete data (matched by key) and re-render.
    function refreshOpenStep() {
      if (flow.hidden || state.step === 'hero') return;
      var next = { step: state.step, backTo: state.backTo };
      if (state.makeGroup) {
        next.makeGroup = freshMakeGroup(state.makeGroup.key) || state.makeGroup;
        if (state.modelGroup) {
          var mg = groupByCI(next.makeGroup.builds, function (b) { return b.model; });
          for (var i = 0; i < mg.length; i++) if (mg[i].key === state.modelGroup.key) next.modelGroup = mg[i];
          if (!next.modelGroup) next.modelGroup = state.modelGroup;
        }
      }
      if (state.wheelGroup) next.wheelGroup = freshWheelGroup(state.wheelGroup.key) || state.wheelGroup;
      state = next;
      render();
    }

    function applyComplete(data) {
      if (data && data.builds && data.builds.length >= builds.length) {
        builds = data.builds;
        if (data.catalog && data.catalog.length) catalog = data.catalog;
        annotate(builds, catalog);
      }
      complete = true;
      appendMissingTiles();
      refreshOpenStep();
      reconcileFeatured();
      var queue = whenCompleteQueue;
      whenCompleteQueue = [];
      queue.forEach(function (fn) { fn(); });
    }

    if (window.DHAGalleryIndex) {
      window.DHAGalleryIndex.load({ fresh: true }).then(applyComplete, function (error) {
        // Keep working from the first page rather than blocking discovery.
        if (window.console && console.warn) console.warn('Gallery: could not load the complete build list.', error);
        applyComplete(null);
      });
    } else {
      applyComplete(null);
    }

    // Wheel deep link (WI-20, see docs/work-items.md): a product page's
    // "See this wheel in the real world" teaser links here as
    // /pages/gallery-builds?wheel=<code> when a wheel has multiple real
    // matching builds (a single match links straight to that build's own
    // Build Detail page instead — no need to land here at all). Reuses
    // resolveWheelResults() unchanged, the exact same function the wheel
    // picker's own onPick calls — so this is just an alternate entry
    // point into the existing flow, not a second implementation of it.
    // resolveWheelResults() waits for the complete set, so a wheel whose
    // builds live beyond the first page resolves correctly too. If the
    // data changed since the product page rendered and only one (or zero)
    // match remains, it already degrades correctly (redirects to Build
    // Detail, or leaves the hero showing). Matched case-insensitively
    // (WI-26) against the same wheel groups the picker itself builds.
    var deepLinkWheel = null;
    try {
      deepLinkWheel = new URLSearchParams(window.location.search).get('wheel');
    } catch (error) {
      deepLinkWheel = null;
    }
    if (deepLinkWheel) {
      var deepLinkKey = normKey(deepLinkWheel);
      whenComplete(function () {
        var group = freshWheelGroup(deepLinkKey);
        if (group) resolveWheelResults(group);
      });
    }
  }

  ready(function () {
    var roots = document.querySelectorAll('[data-dha-gl]');
    for (var i = 0; i < roots.length; i++) initGallery(roots[i]);
  });
})();
